import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ContentLang } from '@/features/editor/editorSlice'
import {
  acquireMeasureSlot,
  designOverflowLevel,
  waitForLayoutReady,
  type DesignOverflowLevel,
} from '@/features/editor/lib/designOverflowMeasure'
import {
  designOverflowCacheKey,
  writeOverflowCache,
  type DesignBlock,
} from '@/features/editor/lib/designOverflowCache'
import { PhoneCanvas } from '../PhoneCanvas'
import { renderBlockPreview } from '../renderBlockPreview'
import { StrictLangContext } from './strictLangContext'

const HIDDEN_PROBE = {
  position: 'fixed',
  left: -10000,
  top: 0,
  width: 390,
  visibility: 'hidden',
  pointerEvents: 'none',
} as const

interface Props {
  block: DesignBlock
  lang: ContentLang
  onResult: (level: DesignOverflowLevel) => void
}

/**
 * Меряет переполнение составного блока в скрытом холсте телефона — как его
 * увидит ученик. Холст поднимается только со слотом замера (их ограниченное
 * число), слот отпускается всегда: по итогу, на ошибке и при размонтировании.
 * Блок и язык читаются один раз — вызывающий ключует зонд по `designOverflowCacheKey`.
 */
export const DesignOverflowProbe = ({ block, lang, onResult }: Props) => {
  const [hasSlot, setHasSlot] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const releaseRef = useRef<(() => void) | null>(null)
  const onResultRef = useRef(onResult)
  onResultRef.current = onResult

  useEffect(() => {
    let alive = true
    void acquireMeasureSlot().then((release) => {
      if (!alive) return release()
      releaseRef.current = release
      setHasSlot(true)
    })
    return () => {
      alive = false
      releaseRef.current?.()
      releaseRef.current = null
    }
  }, [])

  useEffect(() => {
    const root = rootRef.current
    if (!hasSlot || !root) return
    let alive = true
    const measure = async (): Promise<DesignOverflowLevel | null> => {
      try {
        const ready = await waitForLayoutReady(root)
        if (!alive) return null
        const canvas = root.querySelector<HTMLElement>('[data-design-canvas]')
        const level = canvas ? designOverflowLevel(canvas) : 'fits'
        // Таймаут — раскладка могла не осесть: уровень отдаём, но не кешируем,
        // следующий монтаж перемеряет.
        if (!ready?.timedOut) writeOverflowCache(designOverflowCacheKey(block, lang), level)
        return level
      } catch {
        // Сбой замера не держит урок «проверяется» вечно: считаем, что помещается
        // (fail-open) и не кешируем. Страхует только значок на холсте редактора:
        // api переполнение не проверяет (строки `clientOnly`).
        return alive ? 'fits' : null
      }
    }
    void measure().then((level) => {
      // Мёртвый прогон (StrictMode повторяет эффект) слот не трогает: им уже
      // меряет живой, а при настоящем размонтировании слот отпускает эффект выше.
      if (!alive) return
      releaseRef.current?.()
      releaseRef.current = null
      if (level !== null) onResultRef.current(level)
    })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSlot])

  if (!hasSlot) return null
  return createPortal(
    <div ref={rootRef} aria-hidden data-design-overflow-probe style={HIDDEN_PROBE}>
      <PhoneCanvas viewport="phone" fit="width">
        <StrictLangContext.Provider value>
          {renderBlockPreview(block, lang)}
        </StrictLangContext.Provider>
      </PhoneCanvas>
    </div>,
    document.body,
  )
}
